import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { getStatic, remove, set } from '../local-storage';

const NAMESPACE = 'serverEnvironment';
const ORIGIN = 'origin';

const PRODUCTION_ORIGIN = 'https://deckee.com';
const STAGING_ORIGIN = 'https://staging.deckee.com';
const PREVIEW_ORIGIN = /^https:\/\/pr-\d+-preview\.deckee\.com$/;
const REACHABILITY_TIMEOUT_MS = 3000;

const isPersistableOrigin = (origin: unknown): origin is string =>
  origin === STAGING_ORIGIN ||
  (typeof origin === 'string' && PREVIEW_ORIGIN.test(origin));

/**
 * Remembers only staging and numeric preview environments. Production clears the preference;
 * local and arbitrary hosts keep their existing one-off navigation behaviour.
 */
export const switchServerEnvironment = async (
  origin: string,
): Promise<void> => {
  if (origin === PRODUCTION_ORIGIN) {
    await remove(NAMESPACE, ORIGIN, true);
  } else if (isPersistableOrigin(origin) && Capacitor.isNativePlatform()) {
    await set(NAMESPACE, ORIGIN, origin, true);
  }

  window.location.assign(`${origin}/maps`);
};

const isOriginReachable = async (origin: string): Promise<boolean> => {
  try {
    // Native HTTP bypasses CORS so we can see nginx 503s and other error statuses.
    // Browser fetch with no-cors cannot.
    const response = await CapacitorHttp.request({
      url: `${origin}/maps`,
      method: 'GET',
      connectTimeout: REACHABILITY_TIMEOUT_MS,
      readTimeout: REACHABILITY_TIMEOUT_MS,
    });

    return response.status >= 200 && response.status < 300;
  } catch (error) {
    return false;
  }
};

/**
 * Redirects the native app to a remembered staging or preview origin when that host still
 * responds successfully. Unreachable hosts and error statuses (e.g. nginx 503) clear the
 * preference so the next start stays on production. No-op for regular users with nothing stored.
 */
export const applyServerEnvironment = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  const origin = getStatic(NAMESPACE, ORIGIN);
  if (!isPersistableOrigin(origin) || window.location.origin === origin) {
    return false;
  }

  if (!(await isOriginReachable(origin))) {
    await remove(NAMESPACE, ORIGIN, true);
    return false;
  }

  const { pathname, search, hash } = window.location;
  window.location.replace(`${origin}${pathname}${search}${hash}`);

  return true;
};
